import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PhotoGrid from '../components/shared/PhotoGrid';
import FullScreenView from '../components/shared/FullScreenView';
import SelectToggleButton from '../components/ad/SelectToggleButton';
import ConfirmModal from '../components/shared/ConfirmModal';
import ApprovalPhotoCard from '../components/admin/ApprovalPhotoCard';
import StatusMessage, { type FetchState } from '../components/shared/StatusMessage';
import InlineErrorBanner from '../components/shared/InlineErrorBanner';
import BatchListView from '../components/admin/BatchListView';
import { useSelection } from '../context/SelectionContext';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { downloadExport, type ExportProgress } from '../utils/downloadExport';
import ExportProgressBar from '../components/shared/ExportProgressBar';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { getStatusBadge } from '../utils/batchStatus';
import ApprovalTypeBadge from '../components/shared/ApprovalTypeBadge';
import type { Batch, Photo, ApiErrorBody, ApprovalStatus, FinalStatus, ArtistStatus } from '../types';

type ActiveView = null | 'selecting' | 'approval';
type SelectFetchState = { batchId: string | null; error: string | null; photos: Photo[] };
type PostSave = 'none' | 'dialog' | 'banner';
type ReviewRequester = { id: number; fName: string; lName: string };

function setsEqual(a: Set<number>, b: Set<number>): boolean {
    if (a.size !== b.size) return false;
    for (const id of a) if (!b.has(id)) return false;
    return true;
}

export default function ArtisticDirectorGallery() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const batchId = searchParams.get('batchId');
    const { adSelectedIds, setAdSelectedIds } = useSelection();

    const [activeView, setActiveView] = useState<ActiveView>(null);
    const [batches, setBatches] = useState<Batch[]>([]);
    

    // Selecting view state
    const [fetchState, setFetchState] = useState<SelectFetchState>({ batchId: null, error: null, photos: [] });
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [hasBeenSaved, setHasBeenSaved] = useState(false);
    const [savedSelections, setSavedSelections] = useState<Set<number>>(new Set());
    const [postSave, setPostSave] = useState<PostSave>('none');
    const { modal, showConfirm } = useConfirmModal();
    const [reviewRequestMap, setReviewRequestMap] = useState<Map<number, ReviewRequester[]>>(new Map());

    // Approval view state
    const [approvalPhotos, setApprovalPhotos] = useState<Photo[]>([]);
    const [artistStatus, setArtistStatus] = useState<ArtistStatus[]>([]);
    const [photoCreditsMap, setPhotoCreditsMap] = useState<Map<string, ApprovalStatus>>(new Map());
    const [photoStatusMap, setPhotoStatusMap] = useState<Map<number, FinalStatus>>(new Map());
    const [loadState, setLoadState] = useState<FetchState>({ status: 'ready', error: null });
    const [exportingStatus, setExportingStatus] = useState<string | null>(null);
    const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
    const [exportMode, setExportMode] = useState(false);
    const [exportSelectedIds, setExportSelectedIds] = useState<Set<number>>(new Set());
    const [inlineError, setInlineError] = useState('');
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Close post-save dialog on Escape
    useEffect(() => {
        if (postSave !== 'dialog') return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPostSave('banner'); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [postSave]);

    const refreshBatches = useCallback(async () => {
        const data = await apiFetch('/api/batches', { headers: authHeaders() }).then(r => r.json());
        setBatches(Array.isArray(data) ? data : []);
    }, []);

    // Batch list polling — 60s while on the list view
    useEffect(() => {
        if (activeView !== null) return;
        refreshBatches();
        const interval = setInterval(refreshBatches, 60000);
        return () => clearInterval(interval);
    }, [activeView, refreshBatches]);

    // Load photos for selecting view
    useEffect(() => {
        if (!batchId || activeView !== 'selecting') return;
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(res => {
                if (!res.ok) throw new Error(`Server returned ${res.status}`);
                return res.json();
            })
            .then((data: Photo[] | { photos?: Photo[] }) => {
                const fetchedPhotos = Array.isArray(data) ? data : data.photos ?? [];
                const initialSelected = new Set(
                    fetchedPhotos.filter(p => p.isSelected === true).map(p => p.id)
                );
                setAdSelectedIds(initialSelected);
                setSavedSelections(new Set(initialSelected));
                setFetchState({ batchId, error: null, photos: fetchedPhotos });
            })
            .catch(err => setFetchState({ batchId, error: err instanceof Error ? err.message : String(err), photos: [] }));
    }, [batchId, activeView, setAdSelectedIds]);

    // Load photos for approval view
    useEffect(() => {
        if (!batchId || activeView !== 'approval') return;
        setLoadState({ status: 'loading', error: null });
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: Photo[]) => {
                setApprovalPhotos(Array.isArray(data) ? data : []);
                setLoadState({ status: 'ready', error: null });
            })
            .catch(err => setLoadState({ status: 'error', error: err instanceof Error ? err.message : String(err) }));
    }, [batchId, activeView]);

    const fetchArtistStatus = useCallback(() => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/artist-status`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: ArtistStatus[]) => setArtistStatus(Array.isArray(data) ? data : []))
            .catch(err => console.error('artist-status fetch failed:', err instanceof Error ? err.message : String(err)));
    }, [batchId]);

    const fetchPhotoCredits = useCallback(() => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/photo-credits`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: {
                credits?: { photoId: number; userId: number; approvalStatus: ApprovalStatus }[];
                photoStatuses?: { photoId: number; finalStatus: FinalStatus }[];
            }) => {
                const credits = data.credits ?? [];
                const statuses = data.photoStatuses ?? [];
                const credMap = new Map<string, ApprovalStatus>();
                for (const row of credits) credMap.set(`${row.photoId}-${row.userId}`, row.approvalStatus);
                setPhotoCreditsMap(credMap);
                const statusMap = new Map<number, FinalStatus>();
                for (const row of statuses) statusMap.set(row.photoId, row.finalStatus);
                setPhotoStatusMap(statusMap);
            })
            .catch(err => console.error('photo-credits fetch failed:', err instanceof Error ? err.message : String(err)));
    }, [batchId]);

    // 60s flat polling for approval view
    useEffect(() => {
        if (activeView !== 'approval' || !batchId) return;
        fetchArtistStatus();
        fetchPhotoCredits();
        const tick = () => {
            fetchArtistStatus();
            fetchPhotoCredits();
            pollTimerRef.current = setTimeout(tick, 60000);
        };
        pollTimerRef.current = setTimeout(tick, 60000);
        return () => clearTimeout(pollTimerRef.current);
    }, [activeView, batchId, fetchArtistStatus, fetchPhotoCredits]);

    // Fetch review requests for secondary batches
        useEffect(() => {
            if (!batchId || activeView !== 'selecting') return;
            const batch = batches.find(b => String(b.id) === batchId);
            if (batch?.batchType !== 'secondary') { setReviewRequestMap(new Map()); return; }
            apiFetch(`/api/batches/${batchId}/review-requests`, { headers: authHeaders() })
                .then(r => r.json())
                .then((data: { photoId: number; requesters: ReviewRequester[] }[]) => {
                    const map = new Map<number, ReviewRequester[]>();
                    for (const item of data) map.set(item.photoId, item.requesters);
                    setReviewRequestMap(map);
                })
                .catch(() => {});
        }, [batchId, activeView, batches]);

    // ── State reset helpers ───────────────────────────────────────────────────

    const resetSelectingState = () => {
        setFetchState({ batchId: null, error: null, photos: [] });
        setAdSelectedIds(new Set());
        setSavedSelections(new Set());
        setActiveIndex(null);
        setSubmitError('');
        setHasBeenSaved(false);
        setPostSave('none');
        setReviewRequestMap(new Map());
    };

    const resetApprovalState = () => {
        clearTimeout(pollTimerRef.current);
        setApprovalPhotos([]);
        setArtistStatus([]);
        setPhotoCreditsMap(new Map());
        setPhotoStatusMap(new Map());
        setLoadState({ status: 'ready', error: null });
        setExportingStatus(null);
        setExportMode(false);
        setExportSelectedIds(new Set());
        setInlineError('');
    };

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleSelectBatch = (batch: Batch) => {
        if (batch.status === 'uploaded' || batch.status === 'ad_selected') {
            setSearchParams({ batchId: String(batch.id) });
            resetSelectingState();
            setHasBeenSaved(batch.status === 'ad_selected');
            setActiveView('selecting');
        } else if (batch.status === 'pending' || batch.status === 'processed') {
            setSearchParams({ batchId: String(batch.id) });
            resetApprovalState();
            setActiveView('approval');
        }
    };

    const handleBack = () => {
        setSearchParams({});
        resetSelectingState();
        resetApprovalState();
        setActiveView(null);
    };

    const toggleSelection = (photoId: number, newState: boolean) => {
        setAdSelectedIds(prev => {
            const next = new Set(prev);
            if (newState) next.add(photoId); else next.delete(photoId);
            return next;
        });
    };

    const handleSubmitSelections = async () => {
        setSubmitError('');
        if (!batchId) { setSubmitError('No batch selected.'); return; }

        const isSinglePoint = activeBatch?.approvalType === 'single_point';
        const ok = await showConfirm({
            title: isSinglePoint
                ? (hasBeenSaved ? 'Update Photo Approvals?' : 'Approve Photos?')
                : (hasBeenSaved ? 'Update Photo Approvals?' : 'Submit Photo Approvals?'),
            body: isSinglePoint
                ? (hasBeenSaved
                    ? 'Once submitted, the photo administrator will be notified you have made adjustments.'
                    : 'Photos you approve will be marked as final.')
                : (hasBeenSaved
                    ? 'Once submitted, the photo administrator will be notified you have made adjustments.'
                    : 'Once submitted, the admin will be notified and approved photos will be sent to the tagged Artists for review.'),
            confirmLabel: isSinglePoint ? (hasBeenSaved ? 'Update' : 'Approve') : (hasBeenSaved ? 'Update' : 'Submit'),
            isDestructive: false,
        });
        if (!ok) return;

        setSubmitting(true);
        try {
            const res = await apiFetch(`/api/batches/${batchId}/submit-selections`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ selectedPhotoIds: [...adSelectedIds] }),
            });
            if (!res.ok) {
                const data: ApiErrorBody = await res.json().catch(() => ({}));
                setSubmitError(data.message || 'Failed to submit. Please try again.');
                return;
            }
            setSavedSelections(new Set(adSelectedIds));
            setHasBeenSaved(true);
            setBatches(prev => prev.map(b =>
                String(b.id) === batchId ? { ...b, status: 'ad_selected', allPhotosTagged: false } : b
            ));
            setPostSave('dialog');
        } catch {
            setSubmitError('Network error — please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRejectAll = async () => {
        setSubmitError('');
        if (!batchId) { setSubmitError('No batch selected.'); return; }

        const isSinglePoint = activeBatch?.approvalType === 'single_point';
        const ok = await showConfirm({
            title: 'Reject All Photos?',
            body: isSinglePoint
                ? 'You have not approved any photos. None of the photos in this batch will be approved.'
                : 'You have not approved any photos. No photos from this batch will go to Artists for approval.',
            confirmLabel: 'Reject All',
            isDestructive: true,
        });
        if (!ok) return;

        setSubmitting(true);
        try {
            const res = await apiFetch(`/api/batches/${batchId}/submit-selections`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ selectedPhotoIds: [], rejectAll: true }),
            });
            if (!res.ok) {
                const data: ApiErrorBody = await res.json().catch(() => ({}));
                setSubmitError(data.message || 'Failed to submit. Please try again.');
                return;
            }
            setBatches(prev => prev.map(b =>
                String(b.id) === batchId ? { ...b, status: 'ad_selected', adZeroSelection: true } : b
            ));
            setPostSave('dialog');
        } catch {
            setSubmitError('Network error — please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const retryLoad = () => {
        setLoadState({ status: 'loading', error: null });
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: Photo[]) => {
                setApprovalPhotos(Array.isArray(data) ? data : []);
                setLoadState({ status: 'ready', error: null });
            })
            .catch(err => setLoadState({ status: 'error', error: err instanceof Error ? err.message : String(err) }));
    };

    const runExport = async (status: string, path: string, fallbackFilename: string) => {
        setExportingStatus(status);
        setExportProgress(null);
        try {
            await downloadExport(path, fallbackFilename, setExportProgress);
        } catch (err) {
            setInlineError(err instanceof Error ? err.message : String(err));
        } finally {
            setExportingStatus(null);
            setExportProgress(null);
        }
    };

    const handleExportByStatus = (status: string) =>
        runExport(status, `/api/batches/${batchId}/export?status=${status}`, `${activeBatch?.galleryName ?? 'batch'}_${status}.zip`);

    const handleExportSelected = () => {
        if (exportSelectedIds.size === 0) return;
        const ids = Array.from(exportSelectedIds).join(',');
        return runExport('selected', `/api/batches/${batchId}/export?photoIds=${ids}`, `${activeBatch?.galleryName ?? 'batch'}_export.zip`);
    };

    // ── Derived values ────────────────────────────────────────────────────────

    const selectingLoading = batchId !== null && fetchState.batchId !== batchId;
    const { error: selectingError, photos } = fetchState;
    const activePhoto = activeIndex !== null ? photos[activeIndex] : null;
    const activeBatch = batches.find(b => String(b.id) === batchId);
    const hasChanges = !setsEqual(adSelectedIds, savedSelections);
    const activeBatchBadge = activeBatch ? getStatusBadge(activeBatch, 'ad') : null;

    const artistStatusMap = useMemo(
        () => new Map(artistStatus.map(a => [a.userId, a])),
        [artistStatus]
    );

    const approvedCount = approvalPhotos.filter(p =>
        (photoStatusMap.get(p.id) ?? p.finalStatus) === 'approved'
    ).length;

    const rejectedCount = approvalPhotos.filter(p =>
        (photoStatusMap.get(p.id) ?? p.finalStatus) === 'rejected'
    ).length;

    const notSelectedCount = approvalPhotos.filter(p => !p.isSelected).length;

    // ── Render: batch list ────────────────────────────────────────────────────

    if (activeView === null) {
        return (
            <BatchListView
                batches={batches}
                navigate={navigate}
                refreshBatches={refreshBatches}
                onSelectBatch={handleSelectBatch}
                variant='ad'
            />
        );
    }

    // ── Render: approval view ─────────────────────────────────────────────────

    if (activeView === 'approval') {
        return (
            <div className='approval-status'>
                <div className='approval-status-header'>
                    <button className='back-link' onClick={handleBack}>← Batches</button>
                    {activeBatch && (
                        <span className='photos-batch-info'>
                            {' '}{activeBatch.galleryName} · {activeBatch.year}{' '}
                        </span>
                    )}
                    {activeBatch && <ApprovalTypeBadge approvalType={activeBatch.approvalType} />}
                    {activeBatchBadge && (
                        <span className={`batch-status-badge ${activeBatchBadge.cls}`}>
                            {activeBatchBadge.label}
                        </span>
                    )}
                </div>

                {inlineError && <InlineErrorBanner message={inlineError} onDismiss={() => setInlineError('')} />}

                {exportingStatus && <ExportProgressBar progress={exportProgress} />}

                {approvalPhotos.length > 0 && (
                  <div className='approval-status-actions'>
                      {exportMode ? (
                          <>
                              <span className='export-mode-label'>
                                  {exportSelectedIds.size === 0 ? 'Click photos to select' : `${exportSelectedIds.size} selected`}
                              </span>
                              <button className='app-nav-btn app-nav-btn--secondary' onClick={() => {
                                  setExportMode(false);
                                  setExportSelectedIds(new Set());
                              }}>
                                  Cancel
                              </button>
                              <button
                                  className='app-nav-btn'
                                  onClick={handleExportSelected}
                                  disabled={exportSelectedIds.size === 0 || exportingStatus === 'selected'}
                              >
                                  {exportingStatus === 'selected' ? 'Preparing ZIP…' : `Export Selected (${exportSelectedIds.size})`}
                              </button>
                          </>
                      ) : (
                          <>
                              <button className='app-nav-btn app-nav-btn--secondary' onClick={() => setExportMode(true)}>
                                  Select Photos to Export
                              </button>
                              {approvedCount > 0 && (
                                  <button className='app-nav-btn' onClick={() => handleExportByStatus('approved')} disabled={!!exportingStatus}>
                                      {exportingStatus === 'approved' ? 'Preparing ZIP…' : `Download Approved (${approvedCount})`}
                                  </button>
                              )}
                              {rejectedCount > 0 && (
                                  <button className='app-nav-btn app-nav-btn--secondary' onClick={() => handleExportByStatus('rejected')} disabled={!!exportingStatus}>
                                      {exportingStatus === 'rejected' ? 'Preparing ZIP…' : `Download Rejected (${rejectedCount})`}
                                  </button>
                              )}
                              {notSelectedCount > 0 && (
                                  <button className='app-nav-btn app-nav-btn--secondary' onClick={() => handleExportByStatus('not_selected')} disabled={!!exportingStatus}>
                                      {exportingStatus === 'not_selected' ? 'Preparing ZIP…' : `Download Not Selected (${notSelectedCount})`}
                                  </button>
                              )}
                          </>
                      )}
                  </div>
              )}

                {loadState.status !== 'ready' && (
                    <StatusMessage state={loadState} onRetry={retryLoad} />
                )}
                {loadState.status === 'ready' && approvalPhotos.length === 0 && (
                    <p>No photos in this batch.</p>
                )}
                {loadState.status === 'ready' && approvalPhotos.length > 0 && (
                    <div className='photo-grid'>
                        {approvalPhotos.map(photo => (
                            <ApprovalPhotoCard
                              key={photo.id}
                              photo={photo}
                              photoStatusMap={photoStatusMap}
                              photoCreditsMap={photoCreditsMap}
                              artistStatusMap={artistStatusMap}
                              batchIsCommitted={true}
                              exportMode={exportMode}
                              isExportSelected={exportSelectedIds.has(photo.id)}
                              onExportToggle={(id) => setExportSelectedIds(prev => {
                                  const next = new Set(prev);
                                  if (next.has(id)) next.delete(id); else next.add(id);
                                  return next;
                              })}
                          />
                        ))}
                    </div>
                )}

                {modal && <ConfirmModal {...modal} />}
            </div>
        );
    }

    // ── Render: selecting view ────────────────────────────────────────────────

    return (
        <div className='ad-gallery-wrapper'>
            <div className='ad-batch-header'>
                <button className='back-link' onClick={handleBack}>← Batches</button>
                {activeBatch && (
                    <span className='photos-batch-info'>
                        {' '}{activeBatch.galleryName} · {activeBatch.year}{' '}
                    </span>
                )}
                {activeBatch && <ApprovalTypeBadge approvalType={activeBatch.approvalType} />}
                {activeBatchBadge && (
                    <span className={`batch-status-badge ${activeBatchBadge.cls}`}>
                        {activeBatchBadge.label}
                    </span>
                )}
            </div>

            {postSave === 'banner' && (
                <div className='ad-next-steps-banner'>
                    <span>✓ Approvals saved. The Photo Administrator has been notified.</span>
                    <button
                        className='ad-banner-dismiss'
                        onClick={() => setPostSave('none')}
                        aria-label='Dismiss'
                    >
                        ×
                    </button>
                </div>
            )}

            {selectingLoading && <p>Loading photos…</p>}
            {selectingError && <p className='ad-error'>Error: {selectingError}</p>}

            {batchId && !selectingLoading && !selectingError && photos.length === 0 && (
                <p className='ad-empty-state'>No photos found for this batch.</p>
            )}

            {photos.length > 0 && (
                <PhotoGrid
                    photos={photos}
                    onSelect={setActiveIndex}
                    selectedIds={adSelectedIds}
                    columns={4}
                    renderOverlay={(photo) => (
                        <SelectToggleButton
                            photoId={photo.id}
                            isSelected={adSelectedIds.has(photo.id)}
                            onToggle={toggleSelection}
                            compact
                        />
                    )}
                    renderCaption={(photo) => {
                        const requesters = reviewRequestMap.get(photo.id);
                        if (!requesters || requesters.length === 0) return undefined;
                        return (
                            <div className='photo-requester-caption'>
                                {requesters.map(r => `${r.fName} ${r.lName.charAt(0)}.`).join(', ')}
                            </div>
                        );
                    }}
                />
            )}

            {photos.length > 0 && (
                <div className='ad-action-bar'>
                    <p className='ad-selection-count'>
                        {adSelectedIds.size} of {photos.length} approved
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        {submitError && (
                            <span className='ad-error' style={{ marginRight: 12, fontSize: 'var(--text-sm)' }}>
                                {submitError}
                            </span>
                        )}
                        {adSelectedIds.size === 0 && (
                            <button
                                className='user-submit-btn user-submit-btn--danger'
                                onClick={handleRejectAll}
                                disabled={submitting}
                            >
                                {submitting ? 'Saving…' : 'Reject All Photos'}
                            </button>
                        )}
                        <button
                            className='user-submit-btn'
                            onClick={handleSubmitSelections}
                            disabled={submitting || adSelectedIds.size === 0 || (hasBeenSaved && !hasChanges)}
                        >
                            {submitting ? 'Saving…' : activeBatch?.approvalType === 'single_point'
                            ? (hasBeenSaved ? 'Update Approvals' : 'Approve Photos →')
                            : (hasBeenSaved ? 'Update Approvals' : 'Submit Approvals →')}
                        </button>
                    </div>
                </div>
            )}

            <FullScreenView
                photo={activePhoto}
                onClose={() => setActiveIndex(null)}
                onPrev={() => setActiveIndex(i => Math.max(0, (i ?? 0) - 1))}
                onNext={() => setActiveIndex(i => Math.min(photos.length - 1, (i ?? 0) + 1))}
                hasPrev={(activeIndex ?? 0) > 0}
                hasNext={activeIndex !== null && activeIndex < photos.length - 1}
                actionArea={
                    activePhoto && (
                        <div className='ad-fs-action-area'>
                            {(reviewRequestMap.get(activePhoto.id)?.length ?? 0) > 0 && (
                                <div className='fs-requester-bar'>
                                    Requested by:{' '}
                                    {reviewRequestMap.get(activePhoto.id)!
                                        .map(r => `${r.fName} ${r.lName}`)
                                        .join(', ')}
                                </div>
                            )}
                            <SelectToggleButton
                                photoId={activePhoto.id}
                                isSelected={adSelectedIds.has(activePhoto.id)}
                                onToggle={toggleSelection}
                            />
                        </div>
                    )
                }
            />

            {modal && <ConfirmModal {...modal} />}

            {postSave === 'dialog' && (
                <div className='modal-backdrop' onClick={() => setPostSave('banner')}>
                    <div
                        className='modal'
                        role='dialog'
                        aria-modal='true'
                        onClick={e => e.stopPropagation()}
                    >
                        <h2 className='modal-title'>{activeBatch?.approvalType === 'single_point' ? 'Photos Approved' : 'Approvals Saved'}</h2>
                        <div className='modal-body'>
                            {activeBatch?.approvalType === 'single_point'
                                ? 'Your approvals have been saved as final. You can continue to update them at any time until the admin marks the batch as processed.'
                                : 'Your approvals have been saved and the photo administrator has been notified. Approved photos will be sent to the tagged Artists for review. You can continue to update your approvals at any time until the Artist approval emails are sent.'}
                        </div>
                        <div className='modal-actions'>
                            <button
                                className='user-submit-btn'
                                onClick={() => setPostSave('banner')}
                                autoFocus
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}